import type { FunctionComponent, TargetedEvent } from 'preact';
import { useState } from 'preact/hooks';

import { getIsolatedKeyFromWidgetName } from 'Addons/fitmentSearch/isolatedKeys';
import FilterInput from 'Components/smallComponents/filterInput';
import type {
  InputTemplateInvoker,
  RepeaterFunctionInvoker,
  TemplateFunction,
  TemplateFunctionInvoker,
  TemplateResult,
} from 'Components/types';
import {
  discardVehicleValue,
  isolatedDiscardVehicle,
  replaceVehicleValue,
} from 'Core/actions/fitmentSearch/index';
import {
  createAllRequiredFieldsHaveSelectionSelector,
  createFitmentSearchResponseFacetsSelector,
  fitmentSearchFieldsSelector,
} from 'Core/selectors/fitmentSearch/index';
import { useDispatch, useSelector } from 'Core/store';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import { cloneSafe } from 'Utils/components';
interface Chip {
  field: string;
  value: string;
  discardValue: () => void;
}
interface Chips {
  chipsRepeater: RepeaterFunctionInvoker<Chip>;
  discardVehicle: () => void;
}

interface VehicleTile {
  value: string;
  term: string;
  selectValue: () => void;
}
interface TileContainer {
  field: string;
  selectedFields: string[];
  tiles: RepeaterFunctionInvoker<VehicleTile>;
}

interface Params {
  tileContainers: RepeaterFunctionInvoker<TileContainer>;
  chips: TemplateFunctionInvoker<Chips>;
  filterInput: InputTemplateInvoker;
  selectedFields: string[];
}

type Props = {
  template: TemplateFunction<Params>;
  name: string;
  doNotRedirectOnVehicleSelect: boolean;
};

const VehicleTiles: FunctionComponent<Props> = ({ template, name, doNotRedirectOnVehicleSelect }) => {
  const dispatch = useDispatch();
  const [filterValue, setFilterValue] = useState('');

  const isolatedKey = getIsolatedKeyFromWidgetName(name);
  const fitmentSearchFacets = useSelector(createFitmentSearchResponseFacetsSelector(isolatedKey));
  const fitmentFields = useSelector(fitmentSearchFieldsSelector);
  const isAllRequiredFieldHasSelection = useSelector(
    createAllRequiredFieldsHaveSelectionSelector(null),
  ) as boolean;

  if (!fitmentSearchFacets?.length) {
    return null;
  }

  const selectedFacets = fitmentSearchFacets.filter((f) => !!f.selection.length);
  const selectedFields = selectedFacets.map((f) => f.field);

  const chips: TemplateFunctionInvoker<Chips> = (template) => {
    if (!selectedFacets.length) {
      return null;
    }

    const chipsRepeater = selectedFacets.map((facet) => (template) => {
      const [selectedValue] = facet.selection;
      const discardValue = () => dispatch(discardVehicleValue(selectedValue, isolatedKey));

      const component = template.call({ field: facet.field, value: selectedValue.value, discardValue });

      return cloneSafe(component, null);
    }) as RepeaterFunctionInvoker<Chip>;

    const discardVehicle = () => {
      dispatch(isolatedDiscardVehicle(isolatedKey));
      if (isAllRequiredFieldHasSelection) {
        fitmentSearchConfig.onVehicleDiscarded();
      }
    };

    const component = template.call({ chipsRepeater, discardVehicle });

    return cloneSafe(component, null) as TemplateResult;
  };

  const tileContainers = fitmentSearchFacets.map((facet) => (template) => {
    const tilesRepeater = facet.getFilteredValues(filterValue, false).map((value) => (templ) => {
      const selectValue = () => {
        dispatch(
          replaceVehicleValue(value, isolatedKey, fitmentFields, {
            isAutoVehicleSelectionDisabled: fitmentSearchConfig.isAutoVehicleSelectionDisabled,
            sendIsolatedRequest: isolatedKey !== null || fitmentSearchConfig.isAutoVehicleSelectionDisabled,
            vehicleWidgetName: name,
            doNotRedirectOnVehicleSelect,
          }),
        );
        setFilterValue('');
      };

      const component = templ.call({ value: value.value, term: value.term, selectValue });

      return cloneSafe(component, null);
    }) as RepeaterFunctionInvoker<VehicleTile>;

    const component = template.call({ field: facet.field, selectedFields, tiles: tilesRepeater });

    return cloneSafe(component, null);
  }) as RepeaterFunctionInvoker<TileContainer>;

  const filterInput: InputTemplateInvoker = (templ) => {
    const props = {
      template: templ,
      filterValue,
      facetName: '',
      facetField: 'vehicleTiles',
      onChange: (e: TargetedEvent<HTMLInputElement, Event>) => setFilterValue(e.currentTarget.value),
    };
    return (<FilterInput {...props} key="vehicle-tiles" />) as ReturnType<InputTemplateInvoker>;
  };

  return template.call({ tileContainers, chips, filterInput, selectedFields });
};

export default VehicleTiles;
